import { Component, Input } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { blank } from '../../../utils/common';
import { isString } from 'lodash-es';
import { DEFAULT_ICON_SIZE, FieldmagicIconProp, FieldmagicIconSizeProp } from '../../icons/icon.component';

export type ClickEvent = Event;

@Component({
  selector: 'fieldmagic-primary-button',
  template: `
    <button
      class="btn {{ variant$ | async }} fieldmagic-btn {{ customClass }}"
      [ngClass]="klass"
      [disabled]="isInProgress || disabled || denied"
      [class.fieldmagic-btn-lg]="size == 'lg'"
      [class.btn-block]="size == 'full'"
      [class.fieldmagic-btn-icon]="purpose == 'icon'"
      [class.fieldmagic-btn-icon]="purpose == 'delete-icon'"
      [class.fieldmagic-btn-icon]="purpose == 'add-line'"
      [class.fieldmagic-btn-showcase]="showcase"
      [class.fieldmagic-btn-list-action]="purpose == 'list-action'"
      [class.fieldmagic-btn-list-link]="variant == 'list-link'"
      [matTooltip]="'forbidden_action_error' | translate | when: denied:{
        otherwise: tooltip | translate
      }"
      [style.background]="color"
      [style.color]="color | bg_text_color"
      [style.border-color]="color"
    >
      <fieldmagic-icon
        *ngIf="(icon | filled) || isInProgress; else content;"
        [icon]="icon || 'spinner'"
        [spin]="isInProgress"
        [size]="iconSize"
        [position]="iconPosition"
      >
        <ng-container [ngTemplateOutlet]="content"></ng-container>
      </fieldmagic-icon>
      <ng-template #content>
        <ng-content *ngIf="label | blank; else asLabel"></ng-content>
      </ng-template>
      <ng-template #asLabel>
        <span *ngIf="hasLabel">{{ label | translate }}</span>
      </ng-template>
    </button>
  `,
  styleUrls: [
    './primary-button.component.scss',
  ],
})
export class PrimaryButtonComponent {
  /// indicates that the button is work in progress
  @Input() isInProgress: boolean = false;

  /// indicates that the button should be disabled. Difference with the isInProgress, when disabled
  /// it doesn't show any progress indicator
  @Input() disabled: boolean = false;

  /// the icon to display at the left side of the text. This does not affect
  /// the loading indicator
  @Input() icon: FieldmagicIconProp;

  /// a custom classes that will style the button
  @Input() klass: string | {
    [className: string]: boolean;
  };

  /// A simple string that labels the button. If you wish to have complex label
  /// you can use the template slot
  @Input() label: string;

  @Input() variant: FieldmagicPrimaryButtonVariant = 'primary';

  @Input() purpose: FieldmagicPrimaryButtonPurpose = 'generic';

  @Input() iconSize: FieldmagicIconSizeProp = DEFAULT_ICON_SIZE;

  @Input() size: 'lg' | 'md' | 'full' = 'md';

  @Input() denied: boolean = false;

  @Input() tooltip?: string;

  @Input() showcase: boolean = false;

  @Input() color?: string;

  @Input() hasLabel: boolean = true;

  @Input() customClass: string = '';

  @Input() iconPosition: string = 'center';

  readonly variant$ = new BehaviorSubject<string>('btn-primary');

  ngOnInit(): void {
    if (this.variant === 'error') {
      this.variant$.next('btn-danger');
    } else if (this.variant == 'secondary') {
      this.variant$.next('btn-secondary');
    } else if (this.variant == 'success' || this.purpose == 'ai') {
      this.variant$.next('btn-success');
    } else if (this.variant == 'warning' || this.purpose == 'delete') {
      this.variant$.next('btn-warning text-white');
    } else if (
        this.variant == 'link'
        || this.purpose == 'icon'
        || this.purpose == 'widget-action'
        || this.purpose == 'delete-icon'
        || this.purpose == 'add-line'
        || this.variant == 'list-link'
    ) {
      this.variant$.next('btn-link');
    } else {
      this.variant$.next('btn-primary');
    }

    if (this.purpose == 'delete' && blank(this.icon)) {
      this.icon = 'trash-alt';
    }

    if (this.purpose == 'delete-icon' && blank(this.icon)) {
      this.icon = {
        icon: 'trash-alt',
        klass: 'text-warning',
      };
    }

    if (this.purpose == 'delete' && blank(this.label)) {
      this.label = 'delete';
    }

    if (this.purpose == 'save' && blank(this.icon)) {
      this.icon = 'save';
    }

    if (this.purpose == 'save' && blank(this.label)) {
      this.label = 'save';
    }

    if (this.purpose == 'widget-action' && isString(this.icon)) {
      this.icon = [{
        icon: this.icon,
        klass: 'text-success',
      }];
    }

    if (this.purpose == 'ai') {
      // Only set default icon and label if not provided by user
      if (blank(this.icon)) {
        this.icon = 'magic';
      }
      if (blank(this.label)) {
        this.label = 'Generate With AI';
      }
      this.showcase = true;
    }

    if (this.purpose == 'add-line') {
      this.icon = [{
        icon: 'plus-circle',
        klass: 'text-success',
      }];
    }

    if (['pdf_job_ai', 'pdf_checklist_ai'].includes(this.purpose)) {
      this.icon = 'file-pdf';
      this.label = this.purpose == 'pdf_job_ai' ? 'pdf_to_job_ai' : 'pdf_to_checklist_ai';
      this.showcase = true;
    }

    if (this.purpose === 'refresh') {
      this.icon = 'sync-alt';
      this.variant = 'link';
      this.label = 'refresh';
    }
  }
}

type FieldmagicPrimaryButtonPurpose = 'delete'
  | 'generic'
  | 'save'
  | 'widget-action'
  | 'icon'
  | 'delete-icon'
  | 'ai'
  | 'add-line'
  | 'list-action'
  | 'pdf_job_ai'
  | 'pdf_checklist_ai'
  | 'refresh';

type FieldmagicPrimaryButtonVariant = 'error'
  | 'primary'
  | 'secondary'
  | 'success'
  | 'warning'
  | 'link'
  | 'list-link';